import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, ArrowUpRight, Send, KeyRound, FileDown, MessageSquarePlus } from "lucide-react";
import { api, formatApiError } from "@/lib/auth";
import { openInvoicePdf } from "@/lib/downloads";
import { STATUS_LABELS, eur } from "@/lib/constants";
import { fmtDateTime as fmtDT, fmtDate as fmtD } from "@/lib/datetime";
import { toast } from "sonner";
import ClientRecordsTab from "@/pages/admin/ClientRecordsTab";
import ImportHistoricalInvoiceDialog from "@/pages/admin/ImportHistoricalInvoiceDialog";
import InvoiceStatusDialog from "@/pages/admin/InvoiceStatusDialog";

const TABS = [
  { key: "dashboard", label: "Tableau de bord" },
  { key: "messages", label: "Messages" },
  { key: "support", label: "Messages généraux" },
  { key: "orders", label: "Demandes" },
  { key: "invoices", label: "Factures" },
  { key: "fiches", label: "Fiches clients" },
  { key: "clients", label: "Clients" },
  { key: "services", label: "Prestations" },
];

const SUPPORT_STATUS = {
  nouveau: { label: "Nouveau", tone: "text-[#0E7C90]" },
  lu: { label: "Lu", tone: "text-neutral-600" },
  repondu: { label: "Répondu", tone: "text-emerald-700" },
  clos: { label: "Clos", tone: "text-neutral-500" },
};

const PRESTATIONS = {}; // filled from services

function Stat({ label, value, hint }) {
  return (
    <div className="cs-card p-6" data-testid={`admin-stat-${label}`}>
      <div className="cs-eyebrow text-neutral-500">{label}</div>
      <div className="font-serif text-4xl mt-2">{value}</div>
      {hint && <div className="mt-1 text-[12px] font-mono text-neutral-500">{hint}</div>}
    </div>
  );
}

export default function Admin() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "dashboard";
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [q, setQ] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterService, setFilterService] = useState("");
  const [activeClient, setActiveClient] = useState(null);
  const [clientOrders, setClientOrders] = useState([]);
  const [msgOrder, setMsgOrder] = useState(null);
  const [conv, setConv] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [supportThreads, setSupportThreads] = useState([]);
  const [activeSupport, setActiveSupport] = useState(null);
  const [supportReply, setSupportReply] = useState("");
  const [supportSending, setSupportSending] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoiceQ, setInvoiceQ] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [statusInvoice, setStatusInvoice] = useState(null);
  const [clientMsg, setClientMsg] = useState("");
  const [clientMsgCat, setClientMsgCat] = useState("generale");
  const [clientMsgSending, setClientMsgSending] = useState(false);

  const refreshAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/orders").then((r) => setOrders(r.data));
  };

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/orders").then((r) => setOrders(r.data));
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/services").then((r) => setServices(r.data));
    api.get("/support").then((r) => setSupportThreads(r.data));
    api.get("/admin/invoices").then((r) => setInvoices(r.data.invoices || [])).catch(() => {});
  }, []);

  const setTab = (k) => setParams({ tab: k });

  const openConversation = async (o) => {
    setMsgOrder(o);
    setReply("");
    try {
      const { data } = await api.get(`/orders/${o.id}/messages`);
      setConv(data);
      if (o.unread_admin > 0) {
        try { await api.post(`/orders/${o.id}/mark_read`); } catch {}
        setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, unread_admin: 0 } : x)));
        api.get("/admin/stats").then((r) => setStats(r.data));
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !msgOrder) return;
    setSending(true);
    try {
      await api.post(`/orders/${msgOrder.id}/messages`, { text: reply, file_ids: [] });
      const { data } = await api.get(`/orders/${msgOrder.id}/messages`);
      setConv(data);
      setReply("");
      toast.success("Message envoyé — le client est notifié par email");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const openSupport = async (t) => {
    setActiveSupport(t);
    setSupportReply("");
    if ((t.unread_admin || 0) > 0 || t.status === "nouveau") {
      try {
        await api.post(`/support/${t.id}/mark_read`);
        setSupportThreads((prev) => prev.map((x) => x.id === t.id ? { ...x, unread_admin: 0, status: x.status === "nouveau" ? "lu" : x.status } : x));
        api.get("/admin/stats").then((r) => setStats(r.data));
      } catch {}
    }
  };

  const sendSupportReply = async () => {
    if (!supportReply.trim() || !activeSupport) return;
    setSupportSending(true);
    try {
      const { data } = await api.post(`/support/${activeSupport.id}/reply`, { text: supportReply.trim() });
      setSupportReply("");
      setActiveSupport(data);
      setSupportThreads((prev) => prev.map((t) => t.id === data.id ? data : t));
      toast.success("Réponse envoyée — le client est notifié par email");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSupportSending(false); }
  };

  const closeSupport = async () => {
    if (!activeSupport) return;
    if (!window.confirm("Clôturer définitivement cette conversation ?")) return;
    try {
      const { data } = await api.post(`/support/${activeSupport.id}/close`);
      setActiveSupport(data);
      setSupportThreads((prev) => prev.map((t) => t.id === data.id ? data : t));
      toast.success("Conversation clôturée");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const usersById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  const invoiceClientLabel = (inv) => {
    const b = inv.billing || {};
    const composed = [b.company_name, `${b.first_name || ""} ${b.last_name || ""}`.trim()].filter(Boolean).join(" · ");
    if (composed) return composed;
    const u = usersById[inv.user_id] || {};
    return `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || "—";
  };

  const invoiceClientEmail = (inv) => {
    const b = inv.billing || {};
    return b.billing_email || (usersById[inv.user_id] || {}).email || "";
  };

  const filteredInvoices = useMemo(() => {
    const qq = invoiceQ.trim().toLowerCase();
    if (!qq) return invoices;
    return invoices.filter((i) => [
      i.number,
      i.order_reference,
      i.type_label,
      invoiceClientLabel(i),
      invoiceClientEmail(i),
    ].filter(Boolean).some((v) => String(v).toLowerCase().includes(qq)));
  }, [invoices, invoiceQ, users]);

  const filteredOrders = useMemo(() => {
    let arr = orders;
    if (filterStatus) arr = arr.filter((o) => o.status === filterStatus);
    if (filterService) arr = arr.filter((o) => o.service_slug === filterService);
    if (q.trim()) {
      const qq = q.toLowerCase();
      arr = arr.filter((o) => {
        const user = usersById[o.user_id] || {};
        return [o.reference, o.service_name, user.email, user.first_name, user.last_name]
          .filter(Boolean).some((v) => v.toLowerCase().includes(qq));
      });
    }
    return arr;
  }, [orders, filterStatus, filterService, q, usersById]);

  const openClient = async (u) => {
    setActiveClient(u);
    setClientMsg("");
    setClientMsgCat("generale");
    const { data } = await api.get(`/admin/users/${u.id}/orders`);
    setClientOrders(data);
  };

  const sendClientMessage = async () => {
    if (!activeClient || !clientMsg.trim() || clientMsgSending) return;
    setClientMsgSending(true);
    try {
      const { data: newThread } = await api.post(`/admin/users/${activeClient.id}/support`, {
        category: clientMsgCat,
        message: clientMsg.trim(),
      });
      // Refresh threads and open the new one in the Support tab
      const { data: threads } = await api.get("/support");
      setSupportThreads(threads);
      const opened = threads.find((t) => t.id === newThread.id) || newThread;
      setActiveSupport(opened);
      setTab("support");
      setClientMsg("");
      toast.success("Message envoyé — conversation ouverte dans Messages généraux");
    } catch (e) {
      toast.error(formatApiError(e, "Envoi impossible"));
    } finally {
      setClientMsgSending(false);
    }
  };

  return (
    <div className="cs-cream py-10 min-h-[80vh]" data-testid="page-admin">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-black/15 pb-6">
          <div>
            <div className="cs-eyebrow text-[#0E7C90]">Back-office · Charbonier Studio</div>
            <h1 className="cs-h1 mt-2 text-3xl lg:text-4xl">Administration</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/espace/profil"
              data-testid="admin-profile-link"
              className="text-[13px] font-mono uppercase tracking-[0.14em] px-3 py-1.5 border border-black/25 hover:border-black hover:bg-black hover:text-[#FAF8F5] transition-colors inline-flex items-center gap-2"
              title="Modifier mon mot de passe et mes informations"
            >
              <KeyRound size={14} /> Mon profil
            </Link>
            {stats?.unread_admin > 0 && (
              <button
                onClick={() => setTab("messages")}
                className="border border-[#0E7C90] px-3 py-1.5 text-[13px] font-mono uppercase tracking-[0.14em] text-[#0E7C90] hover:bg-[#0E7C90] hover:text-[#FAF8F5] transition-colors"
                data-testid="admin-unread-badge"
              >
                {stats.unread_admin} nouveau{stats.unread_admin > 1 ? "x" : ""} message{stats.unread_admin > 1 ? "s" : ""} →
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <nav className="mt-6 flex flex-wrap gap-x-6 gap-y-2 border-b border-black/15 pb-3">
          {TABS.map((t) => {
            let badge = null;
            if (t.key === "messages" && stats?.unread_admin > 0) badge = stats.unread_admin;
            if (t.key === "support" && stats?.support_unread_admin > 0) badge = stats.support_unread_admin;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                data-testid={`admin-tab-${t.key}`}
                className={`text-sm font-mono uppercase tracking-[0.16em] transition-colors inline-flex items-center gap-2 ${tab === t.key ? "text-[#0E7C90]" : "text-neutral-500 hover:text-black"}`}>
                {t.label}
                {badge !== null && (
                  <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 text-[11px] font-mono bg-[#0E7C90] text-[#FAF8F5] rounded-full">{badge}</span>
                )}
              </button>
            );
          })}
        </nav>

        {tab === "dashboard" && stats && (
          <div className="mt-8" data-testid="admin-dashboard">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat label="Nouvelles demandes" value={stats.by_status?.demande_recue || 0} />
              <Stat label="En cours" value={stats.by_status?.en_cours || 0} />
              <Stat label="En attente client" value={(stats.by_status?.attente_accord_client || 0) + (stats.by_status?.attente_validation || 0) + (stats.by_status?.informations_manquantes || 0)} />
              <Stat label="Terminées" value={stats.by_status?.termine || 0} />
            </div>
            <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat label="Commandes totales" value={stats.total_orders} />
              <Stat
                label="CA des terminées"
                value={eur(stats.revenue_total || 0)}
                hint={stats.revenue_total > 0 ? null : "Aucune donnée pour le moment"}
              />
              <Stat
                label="CA moyen / commande"
                value={stats.revenue_avg > 0 ? eur(stats.revenue_avg) : "—"}
                hint={stats.revenue_avg > 0 ? null : "Aucune donnée pour le moment"}
              />
              <Stat
                label="CA horaire"
                value={stats.hourly_rate_avg ? `${stats.hourly_rate_avg} €/h` : "—"}
                hint={
                  stats.work_minutes_total
                    ? `${stats.work_minutes_total} min saisies · Objectif ≥ 65 €/h`
                    : "Aucune donnée pour le moment"
                }
              />
            </div>

            <div className="mt-10">
              <h2 className="cs-h3 text-2xl">Demandes récentes</h2>
              <div className="mt-4 border-t border-black/15">
                {orders.length === 0 && (
                  <div className="py-8 text-center text-sm text-neutral-500" data-testid="admin-empty-orders">
                    Aucune demande pour le moment.
                    <div className="mt-1 text-[12px] font-mono">Les nouvelles commandes apparaîtront ici.</div>
                  </div>
                )}
                {orders.slice(0, 8).map((o) => (
                  <Link key={o.id} to={`/espace/commande/${o.id}`}
                    className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-4 border-b border-black/15 py-4 px-2 hover:bg-white transition-colors">
                    <span className="font-mono text-xs text-neutral-500 w-24">{o.reference}</span>
                    <div>
                      <div className="font-serif text-lg">{o.service_name}</div>
                      <div className="text-xs font-mono text-neutral-500">{STATUS_LABELS[o.status] || o.status}</div>
                    </div>
                    <span className="font-mono hidden sm:block">{eur(o.price)}</span>
                    <span className="font-mono text-xs text-neutral-500 hidden sm:block">{fmtD(o.created_at)}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "messages" && (
          <div className="mt-8 grid lg:grid-cols-[380px_1fr] gap-8" data-testid="admin-messages">
            <div>
              <h2 className="cs-h3 text-xl">Conversations</h2>
              <p className="text-[12px] font-mono text-neutral-500 mt-1">Les messages non lus apparaissent en premier. Toute réponse envoyée notifie automatiquement le client par email.</p>
              <div className="mt-4 border-t border-black/15 max-h-[70vh] overflow-y-auto">
                {[...orders]
                  .filter((o) => (o.unread_admin || 0) > 0 || o.type !== "quote_request")
                  .sort((a, b) => {
                    const ua = a.unread_admin || 0, ub = b.unread_admin || 0;
                    if (ua !== ub) return ub - ua;
                    return new Date(b.created_at) - new Date(a.created_at);
                  })
                  .slice(0, 60)
                  .map((o) => {
                    const user = usersById[o.user_id] || {};
                    const unread = o.unread_admin || 0;
                    const active = msgOrder?.id === o.id;
                    return (
                      <button
                        key={o.id}
                        onClick={() => openConversation(o)}
                        data-testid={`admin-msg-thread-${o.reference}`}
                        className={`w-full text-left border-b border-black/15 py-3 px-3 text-sm transition-colors ${active ? "bg-white" : "hover:bg-white"}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-neutral-500">{o.reference}</span>
                          {unread > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-mono bg-[#0E7C90] text-[#FAF8F5] rounded-full">{unread}</span>
                          )}
                        </div>
                        <div className="font-serif text-[15px] mt-1 leading-tight">{o.service_name}</div>
                        <div className="font-mono text-[11px] text-neutral-500 mt-0.5">
                          {user.first_name} {user.last_name} · {user.email || "?"}
                        </div>
                      </button>
                    );
                  })}
                {orders.filter((o) => (o.unread_admin || 0) > 0).length === 0 && (
                  <div className="py-6 text-neutral-500 text-sm">Aucun message non lu.</div>
                )}
              </div>
            </div>
            <div>
              {msgOrder ? (
                <div className="flex flex-col h-[70vh]">
                  <div className="flex flex-wrap items-start justify-between gap-3 border-b border-black/15 pb-3">
                    <div>
                      <div className="cs-eyebrow text-neutral-500">{msgOrder.reference} · {STATUS_LABELS[msgOrder.status] || msgOrder.status}</div>
                      <h3 className="cs-h3 mt-1 text-2xl">{msgOrder.service_name}</h3>
                      <div className="mt-1 text-xs font-mono text-neutral-500">
                        {(usersById[msgOrder.user_id] || {}).first_name} {(usersById[msgOrder.user_id] || {}).last_name} · {(usersById[msgOrder.user_id] || {}).email}
                      </div>
                    </div>
                    <Link
                      to={`/espace/commande/${msgOrder.id}`}
                      className="text-[12px] font-mono uppercase tracking-[0.14em] border border-black/70 px-3 py-2 hover:bg-black hover:text-[#FAF8F5] transition-colors inline-flex items-center gap-1"
                      data-testid="admin-msg-open-order"
                    >
                      Ouvrir la fiche <ArrowUpRight size={14} />
                    </Link>
                  </div>
                  <div className="flex-1 overflow-y-auto py-4 space-y-3" data-testid="admin-msg-conversation">
                    {conv.length === 0 && (
                      <div className="text-sm text-neutral-500 border border-dashed border-black/25 p-4">
                        Aucun message pour cette demande. Écrivez le premier — le client en sera notifié par email.
                      </div>
                    )}
                    {conv.map((m) => {
                      const mine = m.author_role === "admin";
                      return (
                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[80%] px-4 py-3 text-[14px] leading-relaxed ${mine ? "bg-[#0E7C90] text-[#FAF8F5]" : "bg-white border border-black/15"}`}>
                            <div className={`font-mono text-[10px] uppercase tracking-[0.16em] mb-1 ${mine ? "text-[#FAF8F5]/70" : "text-neutral-500"}`}>
                              {m.author_name} · {fmtDT(m.created_at)}
                            </div>
                            <div className="whitespace-pre-wrap">{m.text}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="border-t border-black/15 pt-3">
                    <textarea
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendReply(); }}
                      placeholder="Votre réponse — envoyée aussi par email au client…"
                      rows={3}
                      className="cs-input w-full resize-none"
                      data-testid="admin-msg-reply-input"
                    />
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <div className="text-[11px] font-mono text-neutral-500">Le client recevra cette réponse par email · ⌘/Ctrl + Entrée pour envoyer</div>
                      <button
                        onClick={sendReply}
                        disabled={sending || !reply.trim()}
                        data-testid="admin-msg-send"
                        className="cs-btn-primary !py-2 !px-4 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Send size={14} /> Envoyer
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-neutral-500 border border-dashed border-black/25 p-6" data-testid="admin-msg-placeholder">
                  Sélectionnez une conversation à gauche pour lire et répondre au client. Votre réponse est envoyée immédiatement et notifiée par email.
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "support" && (
          <div className="mt-8 grid lg:grid-cols-[380px_1fr] gap-8" data-testid="admin-support">
            <div>
              <h2 className="cs-h3 text-xl">Messages généraux</h2>
              <p className="text-[12px] font-mono text-neutral-500 mt-1">Questions hors commande. Séparé volontairement des conversations liées à un devis / une commande.</p>
              <div className="mt-4 border-t border-black/15 max-h-[70vh] overflow-y-auto">
                {[...supportThreads]
                  .sort((a, b) => {
                    const rank = (s) => s === "nouveau" ? 0 : s === "lu" ? 1 : s === "repondu" ? 2 : 3;
                    const ra = rank(a.status), rb = rank(b.status);
                    if (ra !== rb) return ra - rb;
                    return new Date(b.updated_at) - new Date(a.updated_at);
                  })
                  .map((t) => {
                    const user = usersById[t.user_id] || {};
                    const st = SUPPORT_STATUS[t.status] || SUPPORT_STATUS.nouveau;
                    const active = activeSupport?.id === t.id;
                    return (
                      <button
                        key={t.id}
                        onClick={() => openSupport(t)}
                        data-testid={`admin-support-thread-${t.id}`}
                        className={`w-full text-left border-b border-black/15 py-3 px-3 text-sm transition-colors ${active ? "bg-white" : "hover:bg-white"}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className={`font-mono text-[10px] uppercase tracking-[0.14em] ${st.tone}`}>{st.label}</span>
                          {(t.unread_admin || 0) > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-mono bg-[#0E7C90] text-[#FAF8F5] rounded-full">{t.unread_admin}</span>
                          )}
                        </div>
                        <div className="font-serif text-[15px] mt-1">{t.category_label}</div>
                        <div className="font-mono text-[11px] text-neutral-500 mt-0.5">
                          {user.first_name} {user.last_name} · {user.email || "?"} · {fmtD(t.created_at)}
                        </div>
                        <div className="text-neutral-600 text-[12px] mt-1 line-clamp-1">{t.initial_message}</div>
                      </button>
                    );
                  })}
                {supportThreads.length === 0 && <div className="py-6 text-neutral-500 text-sm">Aucun message général pour le moment.</div>}
              </div>
            </div>
            <div>
              {activeSupport ? (
                <div className="flex flex-col h-[70vh]" data-testid="admin-support-conversation">
                  <div className="flex flex-wrap items-start justify-between gap-3 border-b border-black/15 pb-3">
                    <div>
                      <div className={`cs-eyebrow ${(SUPPORT_STATUS[activeSupport.status] || {}).tone || "text-neutral-500"}`}>
                        {(SUPPORT_STATUS[activeSupport.status] || {}).label} · {fmtD(activeSupport.created_at)}
                      </div>
                      <h3 className="cs-h3 mt-1 text-2xl">{activeSupport.category_label}</h3>
                      <div className="mt-1 text-xs font-mono text-neutral-500">
                        {(usersById[activeSupport.user_id] || {}).first_name} {(usersById[activeSupport.user_id] || {}).last_name} · {(usersById[activeSupport.user_id] || {}).email}
                      </div>
                    </div>
                    {activeSupport.status !== "clos" && (
                      <button
                        onClick={closeSupport}
                        data-testid="admin-support-close"
                        className="text-[12px] font-mono uppercase tracking-[0.14em] border border-black/70 px-3 py-2 hover:bg-black hover:text-[#FAF8F5] transition-colors"
                      >
                        Clôturer la conversation
                      </button>
                    )}
                  </div>
                  <div className="flex-1 overflow-y-auto py-4 space-y-3">
                    {activeSupport.messages.map((m, i) => {
                      const mine = m.author_role === "admin";
                      return (
                        <div key={i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[80%] px-4 py-3 text-[14px] leading-relaxed ${mine ? "bg-[#0E7C90] text-[#FAF8F5]" : "bg-white border border-black/15"}`}>
                            <div className={`font-mono text-[10px] uppercase tracking-[0.16em] mb-1 ${mine ? "text-[#FAF8F5]/70" : "text-neutral-500"}`}>
                              {m.author_name} · {fmtDT(m.created_at)}
                            </div>
                            <div className="whitespace-pre-wrap">{m.text}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {activeSupport.status !== "clos" ? (
                    <div className="border-t border-black/15 pt-3">
                      <textarea
                        value={supportReply}
                        onChange={(e) => setSupportReply(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendSupportReply(); }}
                        placeholder="Votre réponse — envoyée aussi par email au client…"
                        rows={3}
                        className="cs-input w-full resize-none"
                        data-testid="admin-support-reply-input"
                      />
                      <div className="mt-2 flex items-center justify-between gap-3">
                        <div className="text-[11px] font-mono text-neutral-500">⌘/Ctrl + Entrée pour envoyer</div>
                        <button
                          onClick={sendSupportReply}
                          disabled={supportSending || !supportReply.trim()}
                          data-testid="admin-support-send"
                          className="cs-btn-primary !py-2 !px-4 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Send size={14} /> Envoyer
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="border-t border-black/15 pt-3 text-[13px] font-mono text-neutral-500">
                      Conversation clôturée — plus de réponse possible.
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-neutral-500 border border-dashed border-black/25 p-6" data-testid="admin-support-placeholder">
                  Sélectionnez un message général à gauche pour le lire et y répondre. Chaque réponse est envoyée immédiatement au client par email.
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div className="mt-8" data-testid="admin-orders">
            <div className="grid md:grid-cols-[1fr_180px_180px] gap-3">
              <label className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                <input value={q} onChange={(e) => setQ(e.target.value)} className="cs-input pl-9"
                  placeholder="Rechercher référence, prestation, client…" data-testid="admin-search" />
              </label>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="cs-input" data-testid="admin-filter-status">
                <option value="">Tous statuts</option>
                {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <select value={filterService} onChange={(e) => setFilterService(e.target.value)} className="cs-input" data-testid="admin-filter-service">
                <option value="">Toutes prestations</option>
                {services.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
              </select>
            </div>

            <div className="mt-6 border-t border-black/15">
              {filteredOrders.map((o) => {
                const user = usersById[o.user_id] || {};
                return (
                  <Link key={o.id} to={`/espace/commande/${o.id}`}
                    className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-4 border-b border-black/15 py-4 px-2 hover:bg-white transition-colors"
                    data-testid={`admin-order-${o.reference}`}>
                    <span className="font-mono text-xs text-neutral-500 w-28">{o.reference}</span>
                    <div>
                      <div className="font-serif text-lg">{o.service_name}</div>
                      <div className="text-xs font-mono text-neutral-500">
                        {user.first_name} {user.last_name} · {user.email || "?"} · {STATUS_LABELS[o.status] || o.status}
                        {o.end_client && <span className="ml-2 text-[#0E7C90]" data-testid={`admin-order-end-client-${o.reference}`}>· pour {o.end_client.name}{o.billing_party === "end_client" ? " (facturé au client final)" : ""}</span>}
                        {o.unread_admin > 0 && <span className="ml-2 text-[#0E7C90]">● {o.unread_admin} nouveau(x)</span>}
                        {(o.supplements || []).some((s) => s.status === "pending") && <span className="ml-2 text-[#0E7C90]">● supplément en attente</span>}
                      </div>
                    </div>
                    <span className="font-mono hidden sm:block">{eur(o.price)}</span>
                    <span className="font-mono text-xs text-neutral-500 hidden sm:block">{fmtD(o.created_at)}</span>
                  </Link>
                );
              })}
              {filteredOrders.length === 0 && <div className="py-6 text-neutral-500">Aucune demande.</div>}
            </div>
          </div>
        )}

        {tab === "invoices" && (
          <div className="mt-8" data-testid="admin-invoices">
            <ImportHistoricalInvoiceDialog
              open={importOpen}
              onClose={() => setImportOpen(false)}
              onImported={() => api.get("/admin/invoices").then(r => setInvoices(r.data.invoices || []))}
            />
            <InvoiceStatusDialog
              open={!!statusInvoice}
              invoice={statusInvoice}
              onClose={() => setStatusInvoice(null)}
              onSaved={(updated) => setInvoices(invoices.map(x => x.id === updated.id ? updated : x))}
            />
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 className="cs-h3 text-xl">Factures ({invoices.length})</h2>
                <p className="mt-1 text-[13px] font-mono text-neutral-500">
                  Toutes les factures émises · générées automatiquement au paiement ou importées manuellement
                </p>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <button onClick={() => setImportOpen(true)} className="cs-btn-primary !py-2 !px-3 text-[13px] inline-flex items-center gap-1 whitespace-nowrap" data-testid="admin-import-invoice">
                  + Ajouter une facture existante
                </button>
                <label className="relative flex-1 sm:w-80">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <input
                    value={invoiceQ}
                    onChange={(e) => setInvoiceQ(e.target.value)}
                    className="cs-input pl-9 w-full"
                    placeholder="N° facture, commande, client…"
                    data-testid="admin-invoices-search"
                  />
                </label>
              </div>
            </div>

            {filteredInvoices.length === 0 && (
              <div className="cs-card p-8 mt-6 text-center text-sm text-neutral-600" data-testid="admin-invoices-empty">
                {invoices.length === 0 ? "Aucune facture émise pour le moment." : "Aucune facture ne correspond à votre recherche."}
              </div>
            )}

            {filteredInvoices.length > 0 && (
              <div className="cs-card p-0 overflow-hidden mt-6" data-testid="admin-invoices-list">
                {/* Header desktop */}
                <div className="hidden lg:grid grid-cols-[140px_120px_1fr_130px_110px_180px] gap-4 px-5 py-3 border-b border-black/15 text-[11px] font-mono uppercase tracking-[0.14em] text-neutral-500">
                  <div>N° facture</div>
                  <div>Type</div>
                  <div>Client · Commande</div>
                  <div>Date</div>
                  <div className="text-right">Montant</div>
                  <div className="text-right">Actions</div>
                </div>
                {filteredInvoices.map((i) => (
                  <div
                    key={i.id}
                    className="flex flex-col gap-3 px-5 py-4 border-b border-black/10 lg:grid lg:grid-cols-[140px_120px_1fr_130px_110px_180px] lg:gap-4 lg:items-center"
                    data-testid={`admin-invoice-${i.number}`}
                  >
                    <div className="flex items-start justify-between lg:block">
                      <div className="font-mono text-sm inline-flex items-center gap-2">
                        {i.number}
                        {i.historical && <span className="text-[10px] font-mono uppercase tracking-[0.12em] text-amber-700 border border-amber-500 bg-amber-50 px-1.5 py-0.5" data-testid={`admin-invoice-historical-${i.number}`}>Historique</span>}
                      </div>
                      <div className="text-right lg:hidden font-mono text-sm">{eur(i.amount)}</div>
                    </div>
                    <div className="text-[13px] font-mono text-neutral-600">
                      <div>{i.type_label}</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        <span className={`text-[10px] px-1.5 py-0.5 border ${i.payment_status === "paid" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-neutral-400 bg-neutral-50 text-neutral-700"}`}>{i.payment_status === "paid" ? "Payée" : "À régler"}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 border ${i.sent_status === "sent" ? "border-[#0E7C90] bg-[#0E7C90]/10 text-[#0E7C90]" : "border-neutral-400 bg-neutral-50 text-neutral-700"}`}>{i.sent_status === "sent" ? "Envoyée" : "Non envoyée"}</span>
                      </div>
                    </div>
                    <div className="text-sm">
                      <div className="font-serif">{invoiceClientLabel(i)}</div>
                      <div className="text-[12px] font-mono text-neutral-500">
                        {invoiceClientEmail(i)}{invoiceClientEmail(i) && i.order_reference ? " · " : ""}{i.order_reference || ""}
                      </div>
                    </div>
                    <div className="hidden lg:block text-[12px] font-mono text-neutral-600">
                      {i.issued_at ? fmtD(i.issued_at) : "—"}
                    </div>
                    <div className="hidden lg:block text-right font-mono">{eur(i.amount)}</div>
                    <div className="lg:text-right">
                      <div className="flex gap-2 lg:justify-end">
                        <button
                          type="button"
                          onClick={() => openInvoicePdf(i.id)}
                          className="cs-btn-ghost !text-black !border-black !py-2 !px-3 text-[13px] inline-flex items-center gap-1"
                          data-testid={`admin-download-invoice-${i.number}`}
                        >
                          <FileDown size={13} /> PDF
                        </button>
                        <button
                          type="button"
                          onClick={() => setStatusInvoice(i)}
                          className="cs-btn-ghost !text-black !border-black !py-2 !px-3 text-[13px] inline-flex items-center gap-1"
                          data-testid={`admin-edit-status-${i.number}`}
                        >
                          Modifier
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "fiches" && (
          <ClientRecordsTab services={services} />
        )}

        {tab === "clients" && (
          <div className="mt-8 grid lg:grid-cols-[380px_1fr] gap-8" data-testid="admin-clients">
            <div>
              <h2 className="cs-h3 text-xl">Clients ({users.filter((u) => u.role === "client").length})</h2>
              <div className="mt-4 border-t border-black/15">
                {users.filter((u) => u.role === "client").map((u) => (
                  <button key={u.id} onClick={() => openClient(u)}
                    className={`w-full text-left border-b border-black/15 py-3 px-2 text-sm hover:bg-white transition-colors ${activeClient?.id === u.id ? "bg-white" : ""}`}
                    data-testid={`admin-client-${u.id}`}>
                    <div className="font-serif text-lg">{u.first_name} {u.last_name}</div>
                    <div className="font-mono text-xs text-neutral-500">{u.email} · {u.role}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              {activeClient ? (
                <>
                  <div className="cs-eyebrow text-neutral-500">Historique</div>
                  <h3 className="cs-h3 mt-2 text-2xl">{activeClient.first_name} {activeClient.last_name}</h3>
                  <div className="mt-1 text-sm text-neutral-500 font-mono">{activeClient.email}</div>
                  <div className="mt-4 grid sm:grid-cols-3 gap-3">
                    <Stat label="Demandes" value={clientOrders.length} />
                    <Stat label="Terminées" value={clientOrders.filter((o) => o.status === "termine").length} />
                    <Stat label="Total facturé" value={eur(clientOrders.filter((o) => o.status === "termine").reduce((a, b) => a + (b.price || 0), 0))} />
                  </div>
                  <div className="mt-6 border-t border-black/15">
                    {clientOrders.map((o) => (
                      <Link key={o.id} to={`/espace/commande/${o.id}`} className="grid grid-cols-[auto_1fr_auto] gap-3 items-center border-b border-black/15 py-3 px-2 hover:bg-white">
                        <span className="font-mono text-xs text-neutral-500">{o.reference}</span>
                        <span className="font-serif">{o.service_name}</span>
                        <span className="font-mono text-sm">{eur(o.price)}</span>
                      </Link>
                    ))}
                    {clientOrders.length === 0 && <div className="py-4 text-neutral-500 text-sm">Aucune commande.</div>}
                  </div>

                  {/* --- Ouvrir une conversation avec ce client --- */}
                  <div className="mt-8 border-t border-black/15 pt-6" data-testid="admin-client-compose">
                    <div className="flex items-center gap-2">
                      <MessageSquarePlus size={16} className="text-[#0E7C90]" />
                      <h4 className="cs-h3 text-lg">Envoyer un message à ce client</h4>
                    </div>
                    <p className="mt-1 text-[12px] font-mono text-neutral-500">
                      Ouvre une nouvelle conversation dans l'onglet <em>Messages généraux</em>. Le client reçoit un e-mail.
                    </p>
                    <div className="mt-4 grid gap-3">
                      <label className="block">
                        <span className="cs-eyebrow text-neutral-500">Catégorie</span>
                        <select
                          value={clientMsgCat}
                          onChange={(e) => setClientMsgCat(e.target.value)}
                          className="cs-input mt-1 w-full sm:max-w-xs"
                          data-testid="admin-client-compose-category"
                        >
                          <option value="generale">Question générale</option>
                          <option value="administratif">Question administrative</option>
                          <option value="compte">Question sur son compte</option>
                          <option value="site">Problème avec le site</option>
                          <option value="suggestion">Suggestion / retour</option>
                          <option value="mot">Un petit mot</option>
                        </select>
                      </label>
                      <label className="block">
                        <span className="cs-eyebrow text-neutral-500">Votre message</span>
                        <textarea
                          value={clientMsg}
                          onChange={(e) => setClientMsg(e.target.value)}
                          rows={5}
                          maxLength={4000}
                          className="cs-input mt-1 w-full resize-y"
                          placeholder="Bonjour, …"
                          data-testid="admin-client-compose-textarea"
                        />
                      </label>
                      <div className="flex flex-wrap items-center gap-3">
                        <button
                          type="button"
                          onClick={sendClientMessage}
                          disabled={clientMsgSending || !clientMsg.trim()}
                          className="cs-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="admin-client-compose-send"
                        >
                          <Send size={14} /> {clientMsgSending ? "Envoi…" : "Envoyer le message"}
                        </button>
                        <span className="text-[11px] font-mono text-neutral-500">
                          {clientMsg.length}/4000
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-sm text-neutral-500 border border-dashed border-black/25 p-6">Sélectionnez un client pour voir son historique.</div>
              )}
            </div>
          </div>
        )}

        {tab === "services" && (
          <div className="mt-8" data-testid="admin-services">
            <h2 className="cs-h3 text-xl">Prestations ({services.length})</h2>
            <div className="mt-4 border-t border-black/15">
              {services.map((s) => (
                <div key={s.slug} className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-b border-black/15 py-3 px-2 text-sm">
                  <div>
                    <div className="font-serif text-lg">{s.name}</div>
                    <div className="font-mono text-xs text-neutral-500">{s.slug} · {s.family || s.category}</div>
                  </div>
                  <span className="font-mono">à partir de {eur(s.starting_price)}</span>
                  <span className={`font-mono text-xs ${s.active ? "text-[#0E7C90]" : "text-neutral-400"}`}>{s.active ? "Actif" : "Inactif"}</span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[12px] text-neutral-500">Édition des prestations à venir dans une prochaine passe.</p>
          </div>
        )}
      </div>
    </div>
  );
}
